"use client";

// État des boosters d'XP, partagé par tous les endroits qui les montrent.
//
// Un booster ne sert pas qu'aux Reps : une fois actif, il multiplie TOUT l'XP
// gagné sur Believemy (sessions de formation, quiz, Reps, leagues). Il se
// gagne dans les coffres et s'achète en boutique, donc il est visible depuis
// la barre du haut (composant Boost) autant que depuis le rail de /reps
// (RepsBoosterCard) : sans magasin commun, les deux se seraient contredits sur
// le nombre en stock et auraient fait deux appels réseau pour la même réponse.
//
// Le magasin vit au niveau du MODULE, pas d'un contexte React : les deux
// lecteurs (barre du haut et rail /reps) ne partagent aucun ancêtre utile, et
// un provider posé assez haut pour les couvrir aurait fait remonter un état de
// gamification jusqu'à la racine de l'app. Ici les instances se rejoignent
// sans arbre commun, ne font qu'un appel pour deux, et l'état survit au
// démontage du rail quand on quitte /reps.

import { useCallback, useEffect, useState } from "react";

// Fraîcheur du cache. Assez court pour qu'un décompte terminé disparaisse vite
// après un changement de page, assez long pour ne pas rappeler le back à
// chaque navigation interne.
const TTL_MS = 30000;

// Les boosters périment (14 jours en stock côté back) : en deçà de ce délai on
// prévient, sinon on se tait plutôt que d'afficher une échéance lointaine que
// personne ne lira. Même seuil que la carte du rail /reps.
export const EXPIRY_WARNING_DAYS = 3;

let cache = null; // dernier état connu ({ active, inventory })
let cachedAt = 0;
let inFlight = null; // déduplique les appels simultanés (rail + barre du haut)
const listeners = new Set();

function notify() {
    for (const listener of listeners) listener(cache);
}

function fetchState() {
    if (inFlight) return inFlight;
    inFlight = fetch("/api/reps/boosters-state", { method: "POST" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
            if (d && d.boosters) {
                cache = d.boosters;
                cachedAt = Date.now();
                notify();
            }
            return cache;
        })
        .catch(() => cache)
        .finally(() => {
            inFlight = null;
        });
    return inFlight;
}

/**
 * Remplace l'état connu par celui que vient de renvoyer le back (activation,
 * achat). Évite un aller-retour de plus juste pour rafraîchir l'affichage.
 */
export function setBoostersState(state) {
    if (!state) return;
    cache = state;
    cachedAt = Date.now();
    notify();
}

/**
 * Force le prochain lecteur à redemander l'état au back. Appelé quand un
 * booster arrive par un chemin qui ne renvoie pas l'inventaire (drop de coffre,
 * achat en boutique).
 */
export function invalidateBoosters() {
    cachedAt = 0;
    fetchState();
}

/**
 * Regroupe l'inventaire par type de booster (même multiplicateur, même durée).
 *
 * Le stock est présenté par LOTS, pas booster par booster : toutes les sources
 * donnent le même ×2 de 30 min (achat en boutique, coffres), donc cinquante
 * boosters faisaient cinquante lignes identiques.
 *
 * Chaque lot est trié par péremption : le bouton Activer consomme le booster le
 * plus proche de sa date limite, jamais un au hasard, sinon on laisse périmer
 * du stock qu'on avait sous la main.
 */
export function groupBoosters(inventory) {
    const at = (b) => {
        const time = b.expiresAt ? new Date(b.expiresAt).getTime() : NaN;
        // Sans date exploitable, on range en dernier plutôt que de casser le tri.
        return Number.isNaN(time) ? Infinity : time;
    };
    const groups = new Map();
    for (const booster of inventory) {
        const key = `${booster.multiplier}-${booster.durationMinutes}`;
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                multiplier: booster.multiplier,
                durationMinutes: booster.durationMinutes,
                items: [],
            });
        }
        groups.get(key).items.push(booster);
    }

    const soon = Date.now() + EXPIRY_WARNING_DAYS * 24 * 60 * 60 * 1000;
    const list = [...groups.values()].map((group) => {
        const items = [...group.items].sort((a, b) => at(a) - at(b));
        const next = items[0];
        return {
            ...group,
            items,
            count: items.length,
            nextId: next.id,
            nextExpiresAt: next.expiresAt || null,
            expiringSoon: items.filter((b) => at(b) <= soon).length,
        };
    });

    // Le plus intéressant d'abord (multiplicateur, puis durée).
    list.sort(
        (a, b) =>
            b.multiplier - a.multiplier || b.durationMinutes - a.durationMinutes
    );
    return list;
}

/**
 * Décompte long ("1h 05m" / "12m 30s"), pour les panneaux qui ont la place.
 */
export function formatCountdown(targetIso) {
    if (!targetIso) return "";
    const ms = new Date(targetIso).getTime() - Date.now();
    if (ms <= 0) return "0s";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) {
        return `${h}h ${String(m).padStart(2, "0")}m`;
    }
    return `${m}m ${String(sec).padStart(2, "0")}s`;
}

/**
 * Décompte court ("1h05" / "12m"), pour la pastille de la barre du haut : les
 * secondes y clignoteraient à côté du solde de fragments sans rien apprendre.
 */
export function formatCountdownShort(targetIso) {
    if (!targetIso) return "";
    const ms = new Date(targetIso).getTime() - Date.now();
    if (ms <= 0) return "0m";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h${String(m).padStart(2, "0")}`;
    // Sous la minute, "0m" laisserait croire que c'est fini : on montre les
    // secondes, c'est le seul moment où elles disent quelque chose.
    if (m < 1) return `${s}s`;
    return `${m}m`;
}

/**
 * Lit l'état des boosters et permet d'en activer un.
 *
 * @param {boolean} enabled monter le hook sans appeler le back (visiteur non
 *   connecté) : la réponse serait un 401 à chaque page.
 */
export default function useXpBoosters(enabled = true) {
    const [state, setState] = useState(cache);

    useEffect(() => {
        if (!enabled) return undefined;

        const listener = (next) => setState(next);
        listeners.add(listener);

        // Un état frais s'affiche tout de suite ; sinon on redemande.
        if (cache && Date.now() - cachedAt < TTL_MS) setState(cache);
        else fetchState();

        // Mêmes déclencheurs que les autres ressources de la barre du haut :
        // retour sur l'onglet, et signal explicite quand un booster change
        // ailleurs dans l'app (achat en boutique, coffre ouvert).
        const onRefresh = () => {
            if (Date.now() - cachedAt >= TTL_MS) fetchState();
        };
        const onChanged = () => invalidateBoosters();
        window.addEventListener("focus", onRefresh);
        window.addEventListener("reps:boosters-changed", onChanged);
        return () => {
            listeners.delete(listener);
            window.removeEventListener("focus", onRefresh);
            window.removeEventListener("reps:boosters-changed", onChanged);
        };
    }, [enabled]);

    const activate = useCallback(async (boosterId) => {
        const res = await fetch("/api/reps/boosters-activate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ boosterId }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) return false;
        if (data.boosters) setBoostersState(data.boosters);
        else invalidateBoosters();
        return true;
    }, []);

    const refresh = useCallback(() => invalidateBoosters(), []);

    return {
        // null = état inconnu (on n'invente jamais un solde), sinon l'état.
        state,
        active: state ? state.active : null,
        inventory: state && state.inventory ? state.inventory : [],
        activate,
        refresh,
    };
}
